"use client"

import { createContext, useCallback, useContext, useEffect, useRef, useState, type PropsWithChildren } from "react"
import { toast } from "sonner"
import { Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Terminal status indicating a background job has finished (successfully or not).
 */
type JobTerminalStatus = "completed" | "failed" | "timed_out"

/**
 * Result returned by a job's poll function on each tick.
 *
 * - "pending": Job is still running — keep polling.
 * - Object with `status`: Job has reached a terminal state.
 */
export type PollResult =
  | { status: "pending" }
  | { status: JobTerminalStatus; title: string; description?: string }

/**
 * Configuration for a single background job submitted to the provider.
 *
 * This is the contract that any feature (extractions, exports, imports, etc.)
 * implements to plug into the generic polling engine.
 */
export interface BackgroundJobConfig {
  /**
   * Unique identifier for this job. Used for deduplication — if a job with
   * the same ID is already tracked, the new submission is ignored.
   */
  id: string

  /**
   * Human-readable label shown in the loading toast while the job is in progress.
   * e.g. "Creating extraction…", "Exporting dataset…"
   */
  loadingTitle: string

  /**
   * Optional description shown under the loading toast title.
   */
  loadingDescription?: string

  /**
   * Maximum time in milliseconds before the job is considered timed out.
   * If not provided, the job will poll indefinitely (not recommended).
   */
  timeoutMs?: number

  /**
   * Polling interval override in milliseconds for this specific job.
   * If not provided, falls back to the provider's default interval.
   */
  pollIntervalMs?: number

  /**
   * The poll function called on each tick. Must return a PollResult indicating
   * whether the job is still pending or has reached a terminal state.
   *
   * The provider passes the current access token so implementations don't need
   * to manage auth themselves.
   *
   * @param accessToken - The current Auth0 access token.
   * @returns A PollResult indicating the job's current state.
   */
  poll: (accessToken: string) => Promise<PollResult>

  /**
   * Optional callback fired when the job reaches any terminal state
   * (completed, failed, or timed out). Useful for refreshing UI state.
   */
  onSettled?: () => void
}

/**
 * Internal representation of a tracked job (config + runtime state).
 */
interface TrackedJob {
  /** The job configuration provided by the caller. */
  config: BackgroundJobConfig
  /** Toast ID for the in-progress loading toast. */
  toastId: string | number
  /** Timestamp (Date.now()) when tracking started. */
  startedAt: number
}

/**
 * Context value exposed by BackgroundJobsProvider.
 */
interface BackgroundJobsContextValue {
  /**
   * Submit a new background job to be tracked and polled.
   *
   * If a job with the same ID is already in-flight, this call is a no-op.
   * Shows a loading toast immediately and begins polling on the next tick.
   *
   * @param config - The job configuration describing how to poll and what to show.
   */
  submitJob: (config: BackgroundJobConfig) => void

  /**
   * Check whether a specific job (by ID) is currently in-flight.
   *
   * @param jobId - The unique job identifier to check.
   * @returns True if the job is being tracked and has not reached a terminal state.
   */
  isJobInProgress: (jobId: string) => boolean
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const BackgroundJobsContext = createContext<BackgroundJobsContextValue | undefined>(undefined)

/**
 * Hook to access the global background jobs context.
 *
 * @returns The background jobs context value.
 * @throws Error if used outside of BackgroundJobsProvider.
 */
export function useBackgroundJobs(): BackgroundJobsContextValue {
  const context = useContext(BackgroundJobsContext)
  if (context === undefined) {
    throw new Error("useBackgroundJobs must be used within a BackgroundJobsProvider")
  }
  return context
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default polling interval if a job does not specify its own (milliseconds). */
const DEFAULT_POLL_INTERVAL_MS = 10_000

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Generic background jobs provider that manages polling for any number of
 * concurrent async jobs.
 *
 * Lives at the app layout level so jobs persist across page navigation.
 * Each job defines its own poll function, timeout, and toast messages — the
 * provider handles the interval lifecycle, timeout enforcement, toast
 * management, and cleanup.
 *
 * Must be rendered inside GlobalContextProvider (uses useAuth internally).
 *
 * @param children - Child components to render.
 */
export function BackgroundJobsProvider({ children }: PropsWithChildren): React.JSX.Element {
  const auth = useAuth()
  const [trackedJobs, setTrackedJobs] = useState<TrackedJob[]>([])
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  // Ref-based set of job IDs currently tracked — prevents duplicate toasts
  // from React strict mode's double-invocation of state updaters.
  const activeJobIds = useRef<Set<string>>(new Set())

  /**
   * Handle a terminal state for a tracked job.
   * Dismisses the loading toast and shows a success, error, or warning toast.
   */
  const handleTerminal = useCallback(
    (tracked: TrackedJob, result: { status: JobTerminalStatus; title: string; description?: string }): void => {
      toast.dismiss(tracked.toastId)

      if (result.status === "completed") {
        toast.success(result.title, {
          description: result.description,
          duration: Infinity,
        })
      } else if (result.status === "failed") {
        toast.error(result.title, {
          description: result.description,
          duration: Infinity,
        })
      } else if (result.status === "timed_out") {
        toast.error(result.title, {
          description: result.description,
          duration: Infinity,
        })
      }

      tracked.config.onSettled?.()
    },
    []
  )

  /**
   * Single polling tick — checks all tracked jobs and removes those that
   * have reached a terminal state (completed, failed, or timed out).
   */
  const pollAll = useCallback(async (): Promise<void> => {
    const accessToken: string | null = await auth.getAccessToken()
    if (!accessToken || trackedJobs.length === 0) return

    const completed: string[] = []

    for (const tracked of trackedJobs) {
      // Enforce timeout if configured
      if (tracked.config.timeoutMs !== undefined) {
        const elapsed: number = Date.now() - tracked.startedAt
        if (elapsed >= tracked.config.timeoutMs) {
          handleTerminal(tracked, {
            status: "timed_out",
            title: `${tracked.config.loadingTitle.replace(/…$/, "")} timed out.`,
            description: "The operation did not complete in time. Please try again.",
          })
          completed.push(tracked.config.id)
          continue
        }
      }

      try {
        const result: PollResult = await tracked.config.poll(accessToken)

        if (result.status !== "pending") {
          handleTerminal(tracked, result)
          completed.push(tracked.config.id)
        }
      } catch (err) {
        // Network error — log but keep trying on next tick
        console.warn(
          `[BackgroundJobs] Failed to poll job "${tracked.config.id}":`,
          err
        )
      }
    }

    // Remove completed jobs from tracking
    if (completed.length > 0) {
      for (const id of completed) {
        activeJobIds.current.delete(id)
      }
      setTrackedJobs((prev) =>
        prev.filter((t) => !completed.includes(t.config.id))
      )
    }
  }, [auth, trackedJobs, handleTerminal])

  // Start/stop the polling interval based on whether we have anything to track.
  // Uses the smallest pollIntervalMs across all tracked jobs as the tick rate.
  useEffect(() => {
    if (trackedJobs.length === 0) {
      if (intervalRef.current !== undefined) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }
      return
    }

    // Determine the fastest interval needed across all jobs
    const intervalMs: number = Math.min(
      ...trackedJobs.map((t) => t.config.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS)
    )

    // Always recreate the interval when the job list or poll function changes
    if (intervalRef.current !== undefined) {
      clearInterval(intervalRef.current)
    }
    intervalRef.current = setInterval(() => {
      void pollAll()
    }, intervalMs)

    return () => {
      if (intervalRef.current !== undefined) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }
    }
  }, [trackedJobs, pollAll])

  /**
   * Submit a new background job. Shows a loading toast and begins polling.
   * No-op if a job with the same ID is already tracked.
   */
  const submitJob = useCallback((config: BackgroundJobConfig): void => {
    // Ref-based dedup — catches React strict mode double-invocation
    if (activeJobIds.current.has(config.id)) return
    activeJobIds.current.add(config.id)

    const toastId = toast(config.loadingTitle, {
      description: config.loadingDescription ?? "You can continue working.",
      duration: Infinity,
      icon: <Loader2 className="size-4 animate-spin" />,
    })

    setTrackedJobs((prev) => [...prev, { config, toastId, startedAt: Date.now() }])
  }, [])

  /**
   * Check if a specific job is currently in-flight.
   */
  const isJobInProgress = useCallback(
    (jobId: string): boolean => {
      return trackedJobs.some((t) => t.config.id === jobId)
    },
    [trackedJobs]
  )

  const value: BackgroundJobsContextValue = {
    submitJob,
    isJobInProgress,
  }

  return (
    <BackgroundJobsContext.Provider value={value}>
      {children}
    </BackgroundJobsContext.Provider>
  )
}
